import React, { useState, useCallback, useEffect } from 'react';
import { Loader2, X, Paperclip, Image, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { api, ContactAttachment } from '@/lib/api';
import { useFileUpload } from '@/lib/hooks/useFileUpload';

export function ContactAttachments({ contactId }: { contactId: string }) {
  const [attachments, setAttachments] = useState<ContactAttachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { uploads, isUploading, uploadFiles } = useFileUpload({
    defaultCategory: 'media',
    onUploadComplete: async (file) => {
      try {
        await api.addContactAttachment(contactId, file.fileId);
        await fetchAttachments();
      } catch {
        toast({ title: 'Failed to attach file', variant: 'destructive' });
      }
    },
    onUploadError: (fileName, error) => {
      toast({
        title: `Failed to upload ${fileName}`,
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const fetchAttachments = useCallback(async () => {
    try {
      const result = await api.listContactAttachments(contactId);
      setAttachments(result.attachments);
    } catch {
      // Non-critical
    } finally {
      setLoadingAttachments(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      await uploadFiles(files);
      // Reset input so the same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [uploadFiles],
  );

  const handleRemoveAttachment = useCallback(
    async (attachmentId: string) => {
      try {
        await api.removeContactAttachment(contactId, attachmentId);
        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
        toast({ description: 'Attachment removed.' });
      } catch {
        toast({ title: 'Failed to remove attachment', variant: 'destructive' });
      }
    },
    [contactId],
  );

  const handleDownload = useCallback(async (fileId: string) => {
    try {
      const result = await api.getDownloadUrl(fileId);
      window.open(result.downloadUrl, '_blank');
    } catch {
      toast({ title: 'Failed to get download link', variant: 'destructive' });
    }
  }, []);

  const isImageMime = (mime?: string) => mime?.startsWith('image/');

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Attachments</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <Paperclip className="h-3.5 w-3.5 mr-1" />
          )}
          Attach File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Upload progress */}
      {uploads
        .filter((u) => u.status === 'uploading')
        .map((upload) => (
          <div
            key={upload.fileId}
            className="flex items-center gap-2 rounded-md border p-2 text-sm"
          >
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="truncate flex-1">{upload.fileName}</span>
            <span className="text-xs text-muted-foreground">{Math.round(upload.progress)}%</span>
          </div>
        ))}

      {/* Attachments list */}
      {loadingAttachments ? (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          No attachments. Upload images, business cards, or documents.
        </p>
      ) : (
        <div className="space-y-1.5">
          {attachments.map((att) => (
            <div key={att.id} className="group/att flex items-center gap-2 rounded-md border p-2">
              {isImageMime(att.mimeType) ? (
                <Image className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{att.label || att.fileName || 'Attachment'}</p>
                {att.fileSize && (
                  <p className="text-xs text-muted-foreground">{formatFileSize(att.fileSize)}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover/att:opacity-100 transition-opacity"
                onClick={() => handleDownload(att.fileId)}
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover/att:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                onClick={() => handleRemoveAttachment(att.id)}
                title="Remove"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
